import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Check, Loader2, Star, Shield, ChevronLeft, MapPin, AlertTriangle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useOrganizationAccess } from '@/app/hooks/useOrganizationAccess';
import { switchOrganization, getAccessibleSitesInOrg } from '@/app/services/organizationAccessService';
import { UserOrganizationAccess } from '@/app/types/organization_access';
import { Site } from '@/app/types/site';

interface OrganizationSwitcherModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'org' | 'site';

export const OrganizationSwitcherModal: React.FC<OrganizationSwitcherModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const { organizations, currentOrganizationId, isLoading } = useOrganizationAccess();

  // Step management
  const [step, setStep] = useState<Step>('org');
  const [selectedOrg, setSelectedOrg] = useState<UserOrganizationAccess | null>(null);

  // Site selection state
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [siteLoadError, setSiteLoadError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  const switchMutation = useMutation({
    mutationFn: ({ organizationId, siteId }: { organizationId: number; siteId?: number }) =>
      switchOrganization(organizationId, siteId),
    onSuccess: () => {
      toast.success('Switching organization...');
      handleClose();
      // Full page refresh to reload all data for new org context
      setTimeout(() => {
        window.location.reload();
      }, 300);
    },
    onError: (error: Error) => {
      toast.error(`Failed to switch organization: ${error.message}`);
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setStep('org');
      setSelectedOrg(null);
      setAvailableSites([]);
      setLoadingSites(false);
      setSiteLoadError(null);
      setSelectedSiteId(null);
    }, 200);
  };

  const handleSelectOrg = async (org: UserOrganizationAccess) => {
    if (org.organization_id === currentOrganizationId) {
      return; // Already on this org
    }

    setSelectedOrg(org);
    setLoadingSites(true);
    setSiteLoadError(null);
    setAvailableSites([]);

    try {
      const sites = await getAccessibleSitesInOrg(org.organization_id);
      setAvailableSites(sites);

      if (sites.length === 0) {
        setSiteLoadError('no_sites');
      } else {
        // Pre-select the org's default site if it exists, otherwise first site
        const defaultSiteId = org.default_site_id || sites[0]?.id;
        setSelectedSiteId(defaultSiteId);
        setStep('site');
      }
    } catch (error) {
      setSiteLoadError('fetch_error');
      toast.error('Failed to load sites for this organization');
    } finally {
      setLoadingSites(false);
    }
  };

  const handleBack = () => {
    setStep('org');
    setSelectedOrg(null);
    setAvailableSites([]);
    setSiteLoadError(null);
    setSelectedSiteId(null);
  };

  const handleSwitchWithSite = () => {
    if (!selectedOrg || !selectedSiteId) return;
    switchMutation.mutate({
      organizationId: selectedOrg.organization_id,
      siteId: selectedSiteId,
    });
  };

  const formatRoleName = (roleName: string) => {
    return roleName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderOrgSelection = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          Switch Organization
        </DialogTitle>
        <DialogDescription>
          Select an organization to switch your working context.
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          organizations.map((org) => {
            const isCurrent = org.organization_id === currentOrganizationId;
            const isSelecting = selectedOrg?.organization_id === org.organization_id && loadingSites;

            return (
              <button
                key={org.organization_id}
                onClick={() => handleSelectOrg(org)}
                disabled={isCurrent || loadingSites}
                className={cn(
                  "w-full p-4 rounded-xl text-left transition-all duration-200",
                  "border",
                  isCurrent
                    ? "bg-primary/10 border-primary/30 dark:bg-primary/20 dark:border-primary/40"
                    : "bg-card border-border/50 hover:bg-muted/50 hover:border-border",
                  "disabled:cursor-not-allowed"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-medium truncate",
                        isCurrent && "text-primary dark:text-primary"
                      )}>
                        {org.organization.name}
                      </span>
                      {org.is_primary && (
                        <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant="outline" className="text-xs">
                        <Shield className="h-3 w-3 mr-1" />
                        {formatRoleName(org.role.name)}
                      </Badge>
                      {org.organization.gstin && (
                        <span className="text-xs text-muted-foreground">
                          {org.organization.gstin}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {isSelecting ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : isCurrent ? (
                      <div className="flex items-center gap-1.5 text-primary dark:text-primary">
                        <Check className="h-5 w-5" />
                        <span className="text-xs font-medium">Current</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })
        )}

        {/* Show error for no sites inline if an org was selected */}
        {siteLoadError === 'no_sites' && selectedOrg && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No Site Access</AlertTitle>
            <AlertDescription>
              You don't have access to any sites in <strong>{selectedOrg.organization.name}</strong>.
              Please contact your administrator to grant site access.
            </AlertDescription>
          </Alert>
        )}

        {siteLoadError === 'fetch_error' && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error Loading Sites</AlertTitle>
            <AlertDescription>
              Failed to load sites. Please try again.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </>
  );

  const renderSiteSelection = () => (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            disabled={switchMutation.isPending}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Select Site
            </DialogTitle>
            <DialogDescription className="mt-1">
              Choose a site for <strong>{selectedOrg?.organization.name}</strong>
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="mt-4 space-y-2 max-h-[350px] overflow-y-auto">
        {availableSites.map((site) => {
          const isSelected = site.id === selectedSiteId;

          return (
            <button
              key={site.id}
              onClick={() => setSelectedSiteId(site.id)}
              disabled={switchMutation.isPending}
              className={cn(
                "w-full p-4 rounded-xl text-left transition-all duration-200",
                "border",
                isSelected
                  ? "bg-primary/10 border-primary/30 dark:bg-primary/20 dark:border-primary/40"
                  : "bg-card border-border/50 hover:bg-muted/50 hover:border-border",
                "disabled:cursor-not-allowed"
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MapPin className={cn(
                      "h-4 w-4 flex-shrink-0",
                      isSelected ? "text-primary" : "text-muted-foreground"
                    )} />
                    <span className={cn(
                      "font-medium truncate",
                      isSelected && "text-primary dark:text-primary"
                    )}>
                      {site.name}
                    </span>
                  </div>
                  {(site.city || site.state) && (
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      {[site.city, site.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0">
                  {isSelected && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t">
        <Button
          onClick={handleSwitchWithSite}
          disabled={!selectedSiteId || switchMutation.isPending}
          className="w-full"
        >
          {switchMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Switching...
            </>
          ) : (
            <>
              Switch to {selectedOrg?.organization.name}
            </>
          )}
        </Button>
      </div>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        {step === 'org' ? renderOrgSelection() : renderSiteSelection()}
      </DialogContent>
    </Dialog>
  );
};
